<script setup lang="ts">
import { computed, nextTick, useTemplateRef, watch } from 'vue'
import { storeToRefs } from 'pinia'
import ProgressSpinner from 'primevue/progressspinner'
import GoalResultsSection from '@/browse/goals/GoalResultsSection.vue'
import { useUserStore } from '@/authentication/user.store'
import { useContentProfileStore } from '@/contentprofile/contentprofile.store'
import { isGoalLocked } from '@/contentprofile/premium_locks'
import { isGoalPremium } from '@/util/goal_scores'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin'
import { EVENT_NAMES } from '@/tracking/analytics/event_names'
import { sortGoalHits, splitByAccess, withoutLegacyGoals } from '@/browse/goals/goal_results'
import type { GoalHit } from '@/goals/goal_types'
import { RESTRICTION_PREMIUM } from '@/goals/browse_goal_card'
import type { BrowseGoalFilters, StandardsByLongRef } from '@/goals/browse_goal_card'

const props = defineProps<{
  goals: GoalHit[]
  totalResults: number
  isLoading: boolean
  selectedSubject: string
  isKeywordActive: boolean
  algoliaQueryId: string
  standardsByLongRef: StandardsByLongRef
  browseFilters: BrowseGoalFilters
  // A prop rather than an emit so the click is recorded once the page has applied it.
  onRelatedSubject: (subject: string) => Promise<unknown>
}>()

const analytics = getAnalyticsTracker()
const { isAuthenticated, isTrial } = storeToRefs(useUserStore())
const { publishedSubjects } = storeToRefs(useContentProfileStore())

const statusRef = useTemplateRef<HTMLElement>('status')
const trialHeadingRef = useTemplateRef<HTMLElement>('trialHeading')
const premiumHeadingRef = useTemplateRef<HTMLElement>('premiumHeading')

const isFullAccessUser = computed(() => isAuthenticated.value && !isTrial.value)
const isSubjectSelected = computed(() => Boolean(props.selectedSubject))
const shownGoals = computed(() => withoutLegacyGoals(props.goals))
const sortedGoals = computed(() =>
  sortGoalHits(shownGoals.value, { isKeywordActive: props.isKeywordActive, isSubjectSelected: isSubjectSelected.value })
)

function isRestrictedForUser(goal: GoalHit): boolean {
  const isPremiumGoal = isGoalPremium(goal)
  const isLockedByContentProfile = isGoalLocked(publishedSubjects.value, goal)
  return isPremiumGoal || isLockedByContentProfile
}

const goalsByAccess = computed(() => splitByAccess(sortedGoals.value, isRestrictedForUser))
const isResultCountCapped = computed(() => props.totalResults > props.goals.length)
const totalCountPrefix = computed(() => (isResultCountCapped.value && props.goals.length ? 'more than ' : ''))
const premiumLinkPrefix = computed(() => (isResultCountCapped.value ? 'Over ' : ''))
const premiumCountPrefix = computed(() => (isResultCountCapped.value ? 'more than ' : ''))
const COUNT_LINE_CLASSES = 'mb-6 text-[12.6px] text-gray-700'
const statusMessage = computed(() => (props.isLoading ? 'Loading...' : `Found ${props.totalResults} results.`))

const sectionProps = computed(() => ({
  groupBySubject: isSubjectSelected.value,
  selectedSubject: props.selectedSubject,
  algoliaQueryId: props.algoliaQueryId,
  standardsByLongRef: props.standardsByLongRef,
  browseFilters: props.browseFilters
}))

let isAwaitingRelatedSubjectResults = false
let focusBeforeRelatedSubject: Element | null = null

// The event names the subject the link was under, but its filter context must describe the related subject's filters.
async function showRelatedSubject(subject: string) {
  const subjectBeforeChange = props.selectedSubject
  isAwaitingRelatedSubjectResults = true
  focusBeforeRelatedSubject = document.activeElement
  await props.onRelatedSubject(subject)
  analytics.sendEvent(EVENT_NAMES.BROWSE_GOALS.CLICKED_RELATED_SUBJECT, {
    browse_goals_filter_subject: subjectBeforeChange,
    browse_goals_results_related_subject: subject
  })
}

// The related subject's results arrive with a later search, so focus moves to the result
// count only once they land, and only if the user has not moved on in the meantime.
function restoreFocusAfterRelatedSubject() {
  if (!isAwaitingRelatedSubjectResults) {
    return
  }
  isAwaitingRelatedSubjectResults = false
  const isFocusUnchanged = document.activeElement === focusBeforeRelatedSubject
  const isFocusDropped = !focusBeforeRelatedSubject || document.activeElement === document.body
  if (isFocusUnchanged || isFocusDropped) {
    nextTick(() => statusRef.value?.focus())
  }
}

function scrollTo(heading: HTMLElement | null) {
  heading?.scrollIntoView({ behavior: 'smooth' })
  heading?.focus({ preventScroll: true })
}

watch(() => props.isLoading, (isLoading) => {
  if (isLoading) {
    return
  }
  analytics.sendEvent(EVENT_NAMES.BROWSE_GOALS.RETURNED_RESULTS, {
    browse_goals_num_results: props.goals.length,
    algolia_query_id: props.algoliaQueryId
  })
  restoreFocusAfterRelatedSubject()
})
</script>

<template>
  <div>
    <div ref="status" role="status" tabindex="-1" class="sr-only focus:not-sr-only">{{ statusMessage }}</div>
    <div v-if="isLoading" class="flex items-center justify-center text-text-md text-gray-700">
      <ProgressSpinner stroke-width="8" class="!ml-0 !mr-2 h-4 w-4 stroke-gray-700" aria-hidden="true" />
      Loading...
    </div>

    <template v-else-if="isFullAccessUser">
      <div :class="COUNT_LINE_CLASSES">Found {{ totalCountPrefix }}{{ shownGoals.length }} results</div>
      <GoalResultsSection :goals="sortedGoals" v-bind="sectionProps" @related-subject="showRelatedSubject" />
    </template>

    <template v-else>
      <template v-if="goalsByAccess.available.length">
        <h2 ref="trialHeading" tabindex="-1" class="mb-1 mt-6 text-display-sm text-gray-900">Available with Trial</h2>
        <div :class="COUNT_LINE_CLASSES" aria-live="polite">
          Found {{ goalsByAccess.available.length }} results
          <template v-if="goalsByAccess.available.length < shownGoals.length">
            (<a href="#premium-results" class="text-blue-600 underline" @click.prevent="scrollTo(premiumHeadingRef)">{{ premiumLinkPrefix }}{{ goalsByAccess.restricted.length }} more with premium</a>)
          </template>
        </div>
        <GoalResultsSection :goals="goalsByAccess.available" v-bind="sectionProps" :related-heading-level="3" @related-subject="showRelatedSubject" />
      </template>

      <template v-if="goalsByAccess.restricted.length">
        <h2 id="premium-results" ref="premiumHeading" tabindex="-1" class="mb-1 mt-6 text-display-sm text-gray-900">Premium Content</h2>
        <div :class="COUNT_LINE_CLASSES" aria-live="polite">
          Found {{ premiumCountPrefix }}{{ goalsByAccess.restricted.length }} results
          <template v-if="goalsByAccess.available.length">
            (<button type="button" class="text-blue-600 underline" @click="scrollTo(trialHeadingRef)">back to trial results</button>)
          </template>
        </div>
        <GoalResultsSection
          :goals="goalsByAccess.restricted"
          v-bind="sectionProps"
          :related-heading-level="3"
          :restriction-type="RESTRICTION_PREMIUM"
          @related-subject="showRelatedSubject"
        />
      </template>
    </template>
  </div>
</template>
